import type { AiSuggestion, AppState, DailyItem, DisciplioSettings, MonthlyGoal, WeeklyGoal } from "./types";
import { parseTimeRange } from "./calendar-utils";

type Language = "en" | "pl" | "uk";

type ZonedDateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
};

function capitalizeFirst(value: string): string {
  if (!value) return value;
  return value.charAt(0).toLocaleUpperCase() + value.slice(1);
}

export function getTodayKey(settings?: DisciplioSettings, now = new Date()): string {
  return formatDateKey(getOperationalDate(settings, now), settings?.timezone);
}

function dateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function localDateKey(date: Date): string {
  return dateKey(date);
}

export function dateFromDateKey(value: string): Date {
  const [yearRaw, monthRaw, dayRaw] = value.split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

export function isDateKey(value: string | null | undefined): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  return localDateKey(dateFromDateKey(value)) === value;
}

function formatDateKey(date: Date, timeZone?: string): string {
  if (!timeZone) {
    return dateKey(date);
  }
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);
    const values = parts.reduce<Record<string, string>>((acc, part) => {
      if (part.type === "year" || part.type === "month" || part.type === "day") {
        acc[part.type] = part.value;
      }
      return acc;
    }, {});
    if (!values.year || !values.month || !values.day) return dateKey(date);
    return `${values.year}-${values.month}-${values.day}`;
  } catch {
    return dateKey(date);
  }
}

function zonedDateParts(date: Date, timeZone?: string): ZonedDateParts {
  if (!timeZone) {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
    };
  }
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(date);
    const values = parts.reduce<Record<string, string>>((acc, part) => {
      if (part.type === "year" || part.type === "month" || part.type === "day" || part.type === "hour" || part.type === "minute") {
        acc[part.type] = part.value;
      }
      return acc;
    }, {});
    return {
      year: Number(values.year ?? date.getFullYear()),
      month: Number(values.month ?? date.getMonth() + 1),
      day: Number(values.day ?? date.getDate()),
      hour: Number(values.hour ?? date.getHours()),
      minute: Number(values.minute ?? date.getMinutes()),
    };
  } catch {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
    };
  }
}

function minutesFromParts(parts: ZonedDateParts): number {
  return parts.hour * 60 + parts.minute;
}

function getCurrentMinutes(settings?: DisciplioSettings, now = new Date()): number {
  return minutesFromParts(zonedDateParts(now, settings?.timezone));
}

export function currentTimeMinutes(settings?: DisciplioSettings, now = new Date()): number {
  return getCurrentMinutes(settings, now);
}

export function getOperationalDate(settings?: DisciplioSettings, now = new Date()): Date {
  const startMinutes = parseTimeToMinutes(settings?.dayStartTime);
  if (getCurrentMinutes(settings, now) >= startMinutes) {
    return new Date(now);
  }
  return previousZonedDay(now, settings?.timezone);
}

/**
 * The instant one *calendar* day earlier in `timeZone`.
 *
 * Subtracting a fixed 24h is not the previous calendar day across a DST transition: the day after
 * a spring-forward is only 23 hours long, so for any local time before 01:00 a 24h step lands two
 * calendar days back. That is the window where the browser's operational date silently disagreed
 * with the server's (OperationalDayService subtracts a calendar day, not 24 hours).
 */
function previousZonedDay(now: Date, timeZone?: string): Date {
  const target = shiftDateKey(formatDateKey(now, timeZone), -1);
  const candidate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  if (formatDateKey(candidate, timeZone) === target) return candidate;
  for (const offsetHours of [-2, -1, 1, 2]) {
    const adjusted = new Date(candidate.getTime() + offsetHours * 60 * 60 * 1000);
    if (formatDateKey(adjusted, timeZone) === target) return adjusted;
  }
  return candidate;
}

export function isOperationalReflectionWindowOpen(settings: DisciplioSettings, now = new Date()): boolean {
  const currentMinutes = getCurrentMinutes(settings, now);
  const startMinutes = parseTimeToMinutes(settings.dayStartTime);
  if (currentMinutes < startMinutes) return true;
  return currentMinutes >= settings.reflectionUnlockHour * 60;
}

function parseTimeToMinutes(value?: string): number {
  if (!value) return 0;
  const match = value.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return 0;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return 0;
  return Math.max(0, Math.min(23, hour)) * 60 + Math.max(0, Math.min(59, minute));
}

export function shiftDateKey(key: string, days: number): string {
  const date = new Date(`${key}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
}

export function formatLongDate(locale = "en-GB", settings?: DisciplioSettings): string {
  const operationalDate = getOperationalDate(settings);
  try {
    return operationalDate.toLocaleDateString(locale, {
      timeZone: settings?.timezone,
      weekday: "long",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return operationalDate.toLocaleDateString(locale, {
      weekday: "long",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }
}

export function formatDateParts(locale = "en-GB", settings?: DisciplioSettings): { weekday: string; full: string } {
  const now = getOperationalDate(settings);
  const weekdayOptions = {
    weekday: "long" as const,
    timeZone: settings?.timezone,
  };
  const dateOptions = {
    day: "numeric" as const,
    month: "long" as const,
    year: "numeric" as const,
    timeZone: settings?.timezone,
  };
  return {
    weekday: capitalizeFirst(
      (() => {
        try {
          return now.toLocaleDateString(locale, weekdayOptions);
        } catch {
          return now.toLocaleDateString(locale, { weekday: "long" });
        }
      })()
    ),
    full: (() => {
      try {
        return now.toLocaleDateString(locale, dateOptions);
      } catch {
        return now.toLocaleDateString(locale, {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
      }
    })(),
  };
}

export function clamp(num: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, num));
}

function timestamp(): string {
  return new Date().toISOString();
}

export function touchState<T extends AppState>(state: T): T {
  return {
    ...state,
    updatedAt: timestamp(),
  };
}

export function toSortableTime(time: string): number {
  if (!time) return Number.POSITIVE_INFINITY;
  const parsed = parseTimeRange(time);
  return parsed ? parsed.startMinutes : Number.POSITIVE_INFINITY;
}

export function dailyCompletion(state: AppState): { total: number; done: number; percent: number } {
  const total = state.dailyItems.length;
  const done = state.dailyItems.filter((i) => i.done).length;
  return { total, done, percent: total ? Math.round((done / total) * 100) : 0 };
}

export function goalById(state: AppState, id: string): WeeklyGoal | MonthlyGoal | undefined {
  return state.weeklyGoals.find((goal) => goal.id === id) ?? state.monthlyGoals.find((goal) => goal.id === id);
}

type SuggestionScope = "day" | "week";

export function generateAiSuggestions(
  state: AppState,
  language: Language = "en",
  scope: SuggestionScope = "day"
): AiSuggestion[] {
  const sourceGoals =
    scope === "week"
      ? state.monthlyGoals.filter((goal) => !goal.completed && goal.target > 0 && goal.unit.trim().length > 0)
      : state.weeklyGoals.filter((goal) => !goal.completed);
  const sourcePrefix = scope === "week" ? "month-gap" : "goal-gap";
  const sourceLabelPrefix =
    language === "pl"
      ? scope === "week"
        ? "Źródło: miesięczny postęp"
        : "Źródło: tygodniowy postęp"
      : language === "uk"
        ? scope === "week"
          ? "Джерело: місячний прогрес"
          : "Джерело: тижневий прогрес"
        : scope === "week"
          ? "Source: monthly progress"
          : "Source: weekly progress";
  return sourceGoals
    .map((goal) => {
      const remaining = Math.max(goal.target - goal.progress, 0);
      const ratio = goal.target ? goal.progress / goal.target : 0;
      return { goal, remaining, ratio };
    })
    .sort((a, b) => a.ratio - b.ratio)
    .slice(0, 3)
    .filter(({ goal }) => !state.dismissedSuggestionIds.includes(`${sourcePrefix}:${goal.id}`))
    .map(({ goal, remaining }, index) => {
      const suggestedAmount = Number((remaining / 2).toFixed(1));
      const suggestedTime = index === 0 ? "09:00-10:00" : index === 1 ? "13:00-14:00" : "";
      const title =
        language === "pl"
          ? scope === "week"
            ? `Ustal jeden mierzalny priorytet tygodnia dla „${goal.title}”`
            : `Domknij jeden konkretny krok dla „${goal.title}”`
          : language === "uk"
            ? scope === "week"
              ? `Визнач один вимірюваний тижневий пріоритет для "${goal.title}"`
              : `Заверши один конкретний крок для "${goal.title}"`
            : scope === "week"
              ? `Define one measurable weekly priority for "${goal.title}"`
              : `Finish one concrete step for "${goal.title}"`;
      const reason =
        language === "pl"
          ? scope === "week"
            ? `${goal.title} nadal nie ma wyraznego miejsca w tym tygodniu. Dodaj jeden mierzalny priorytet tygodniowy o wartosci ${suggestedAmount} ${goal.unit}, ktory bezposrednio pchnie ten cel miesieczny.`
            : `${goal.title} nadal ma widoczna luke ${goal.progress}/${goal.target} ${goal.unit}. Zaplanuj jeden kolejny krok o wartosci ${suggestedAmount} ${goal.unit}, ktory da sie wykonac i odnotowac jeszcze dzis.`
          : language === "uk"
            ? scope === "week"
              ? `${goal.title} досі не має виразного місця в плані цього тижня. Додай один вимірюваний тижневий пріоритет на ${suggestedAmount} ${goal.unit}, який прямо просуне цю місячну ціль.`
              : `${goal.title} все ще має видиму прогалину ${goal.progress}/${goal.target} ${goal.unit}. Заплануй один наступний крок на ${suggestedAmount} ${goal.unit}, який можна виконати і зафіксувати ще сьогодні.`
            : scope === "week"
              ? `${goal.title} still does not have a clear place in this week's plan. Add one measurable weekly priority worth ${suggestedAmount} ${goal.unit} that directly advances this monthly goal.`
              : `${goal.title} still has a visible gap of ${goal.progress}/${goal.target} ${goal.unit}. Schedule one next step worth ${suggestedAmount} ${goal.unit} that can be completed and logged today.`;
      const sourceLabel = scope === "week" ? goal.title : `${sourceLabelPrefix} ${goal.progress}/${goal.target} ${goal.unit}`;
      const confidenceLabel =
        language === "pl" ? "Pewność: wysoka" : language === "uk" ? "Впевненість: висока" : "Confidence: high";
      return {
        id: `${sourcePrefix}:${goal.id}`,
        title,
        reason,
        sourceLabel,
        confidenceLabel,
        prefill: {
          title: goal.title,
          type: "goal",
          time: suggestedTime,
          weeklyGoalId: goal.id,
          contributionAmount: suggestedAmount,
          contributionUnit: goal.unit,
          repeat: { cadence: "none", interval: 1 },
        },
      };
    });
}

export function updateWeeklyGoal(
  state: AppState,
  goalId: string,
  patch: Partial<Pick<WeeklyGoal, "title" | "target" | "unit" | "category" | "completed">>
): AppState {
  const normalizedCategory = typeof patch.category === "string" ? patch.category.trim() : patch.category;
  const nextGoals = state.weeklyGoals.map((goal) =>
    goal.id === goalId
      ? {
          ...goal,
          ...patch,
          title: (patch.title ?? goal.title).trim(),
          target: patch.target ?? goal.target,
          unit: (patch.unit ?? goal.unit).trim(),
          category: normalizedCategory || undefined,
          completed: patch.completed ?? goal.completed,
        }
      : goal
  );
  const weeklyGoalCategories = normalizedCategory
    ? Array.from(new Set([...state.weeklyGoalCategories, normalizedCategory]))
    : state.weeklyGoalCategories;
  return touchState({ ...state, weeklyGoals: nextGoals, weeklyGoalCategories });
}

export function deleteWeeklyGoal(state: AppState, goalId: string): AppState {
  const weeklyGoals = state.weeklyGoals.filter((goal) => goal.id !== goalId);
  const dailyItems = state.dailyItems.map((item) =>
    item.weeklyGoalId === goalId
      ? {
          ...item,
          weeklyGoalId: "",
          contributionAmount: 0,
          contributionUnit: "",
          contributed: false,
        }
      : item
  );
  return touchState({ ...state, weeklyGoals, dailyItems });
}

export function aiRecap(state: AppState, language: Language = "en"): string {
  const { total, done, percent } = dailyCompletion(state);
  const feedback = state.userFeedback.trim();
  const cadence =
    percent >= 80
      ? language === "pl"
        ? "Mocny rytm wykonania."
        : language === "uk"
          ? "Сильний ритм виконання."
          : "Strong execution rhythm."
      : percent >= 50
        ? language === "pl"
          ? "Stabilne wykonanie z miejscem na jeszcze jeden mocny ruch."
          : language === "uk"
            ? "Стабільне виконання з місцем ще для одного сильного кроку."
            : "Balanced execution with room for one more push."
        : language === "pl"
          ? "Niska realizacja dzisiaj. Ogranicz zakres i zabezpiecz jeden blok skupienia jutro."
          : language === "uk"
            ? "Низьке виконання сьогодні. Зменш обсяг і захисти один блок фокусу на завтра."
            : "Low completion today. Reduce scope and protect one focus block tomorrow.";

  return feedback
    ? language === "pl"
      ? `Ukończono ${done}/${total} działań (${percent}%). ${cadence} Twoja notatka mówi: "${feedback}". Rekomendacja AI: zachowaj pierwsze 90 minut jutra na najbardziej strategiczny cel i zaplanuj jeden nawyk regeneracyjny wieczorem.`
      : language === "uk"
        ? `Виконано ${done}/${total} дій (${percent}%). ${cadence} Твоя нотатка каже: "${feedback}". Рекомендація AI: залиш перші 90 хвилин завтра для найстратегічнішої цілі та заплануй одну відновлювальну звичку ввечері.`
        : `You completed ${done}/${total} actions (${percent}%). ${cadence} Your note says: "${feedback}". AI recommendation: keep tomorrow's first 90 minutes for the most strategic goal and plan one recovery habit in the evening.`
    : language === "pl"
      ? `Ukończono ${done}/${total} działań (${percent}%). ${cadence} Dodaj krótką refleksję, aby jutro poprawić jakość rekomendacji.`
      : language === "uk"
        ? `Виконано ${done}/${total} дій (${percent}%). ${cadence} Додай коротку рефлексію, щоб завтра покращити якість рекомендацій.`
        : `You completed ${done}/${total} actions (${percent}%). ${cadence} Add a short reflection to improve recommendation quality tomorrow.`;
}

export function withTodayStreakFlag(state: AppState): AppState {
  const todayKey = getTodayKey(state.settings);
  const done = state.dailyItems.some((i) => i.done);
  return touchState({
    ...state,
    completionHistory: { ...state.completionHistory, [todayKey]: done },
  });
}

export function dismissSuggestion(state: AppState, suggestionId: string): AppState {
  if (state.dismissedSuggestionIds.includes(suggestionId)) return state;
  return touchState({
    ...state,
    dismissedSuggestionIds: [...state.dismissedSuggestionIds, suggestionId],
  });
}

export function toggleDailyItem(state: AppState, itemId: string, checked: boolean): AppState {
  const item = state.dailyItems.find((i) => i.id === itemId);
  if (!item) return state;

  let weeklyGoals = state.weeklyGoals;
  let monthlyGoals = state.monthlyGoals;
  let contributed = item.contributed;

  if (item.weeklyGoalId && item.contributionAmount > 0) {
    if (checked && !item.contributed) {
      const next = applyGoalContributionById(state, item.weeklyGoalId, item.contributionAmount, 1);
      weeklyGoals = next.weeklyGoals;
      monthlyGoals = next.monthlyGoals;
      contributed = true;
    } else if (!checked && item.contributed) {
      const next = applyGoalContributionById(state, item.weeklyGoalId, item.contributionAmount, -1);
      weeklyGoals = next.weeklyGoals;
      monthlyGoals = next.monthlyGoals;
      contributed = false;
    }
  }

  const dailyItems: DailyItem[] = state.dailyItems.map((i) =>
    i.id === itemId ? { ...i, done: checked, contributed } : i
  );

  return withTodayStreakFlag({ ...state, weeklyGoals, monthlyGoals, dailyItems });
}

function applyGoalContributionById(
  state: AppState,
  goalId: string,
  amount: number,
  sign: 1 | -1
): Pick<AppState, "weeklyGoals" | "monthlyGoals"> {
  const weeklyGoals = state.weeklyGoals.map((goal) =>
    goal.id === goalId ? { ...goal, progress: Math.max(0, goal.progress + sign * amount) } : goal
  );
  const monthlyGoals = state.monthlyGoals.map((goal) =>
    goal.id === goalId ? { ...goal, progress: Math.max(0, goal.progress + sign * amount) } : goal
  );
  return { weeklyGoals, monthlyGoals };
}

export function updateDailyItem(
  state: AppState,
  itemId: string,
  patch: Partial<Pick<DailyItem, "title" | "type" | "time" | "weeklyGoalId" | "contributionAmount" | "contributionUnit" | "repeat" | "done">>
): AppState {
  const current = state.dailyItems.find((i) => i.id === itemId);
  if (!current) return state;

  const nextItem: DailyItem = {
    ...current,
    ...patch,
    title: (patch.title ?? current.title).trim(),
  };

  let weeklyGoals = state.weeklyGoals;
  let monthlyGoals = state.monthlyGoals;

  if (current.done && current.contributed) {
    const next = applyGoalContributionById(state, current.weeklyGoalId, current.contributionAmount, -1);
    weeklyGoals = next.weeklyGoals;
    monthlyGoals = next.monthlyGoals;
  }

  const nextContributed = !!(nextItem.done && nextItem.weeklyGoalId && nextItem.contributionAmount > 0);
  if (nextContributed) {
    const next = applyGoalContributionById(
      { ...state, weeklyGoals, monthlyGoals },
      nextItem.weeklyGoalId,
      nextItem.contributionAmount,
      1
    );
    weeklyGoals = next.weeklyGoals;
    monthlyGoals = next.monthlyGoals;
  }

  const dailyItems = state.dailyItems.map((item) =>
    item.id === itemId ? { ...nextItem, contributed: nextContributed } : item
  );

  return withTodayStreakFlag({ ...state, weeklyGoals, monthlyGoals, dailyItems });
}

export function deleteDailyItem(state: AppState, itemId: string): AppState {
  const current = state.dailyItems.find((i) => i.id === itemId);
  if (!current) return state;

  const nextGoals =
    current.done && current.contributed
      ? applyGoalContributionById(state, current.weeklyGoalId, current.contributionAmount, -1)
      : { weeklyGoals: state.weeklyGoals, monthlyGoals: state.monthlyGoals };
  const dailyItems = state.dailyItems.filter((i) => i.id !== itemId);

  return withTodayStreakFlag({ ...state, weeklyGoals: nextGoals.weeklyGoals, monthlyGoals: nextGoals.monthlyGoals, dailyItems });
}
